import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { Button, Icon } from '@iq/react-components';
import { capitalize } from '../../../../../utils';

import ItemHandler from '../ItemHandler';
import ManagePanels from '../ManagePanels';
import Delete from '../../../../Delete';
import JsonEditor from '../../../../JsonEditor';
import Heading from '../../../../Heading';
import StyledItem from '../../../../StyledItem';
import TypeLabel from '../../../../TypeLabel';

import columnSchema from '../../schemas/columnSchema';
import {
  getDefaultPanelType,
  getFilteredPanelTypes,
  uniquePerPagePanelTypes,
} from '../../../../panels/panelSchema';
import { getActiveSite } from '../../../../../bundles/sites';
import { getPagePanelTypeCounts } from '../../../../../bundles/layouts/selectors';

const DEFAULT_PANEL_TITLE = 'Untitled panel';

// To remove duplicate error message
const transformErrors = (errors) =>
  errors.map((error) => {
    if (error.name === 'required' && error.property === 'title') {
      error.message = '';
    }

    return error;
  });

const ColumnItem = ({
  activeTree = {},
  columnCount,
  moveColumn,
  removeColumn,
  pageIndex,
  sectionIndex,
  columnIndex,
  updateColumn,
  columnActive,
  setActiveTreeIndexes,
  panels = [],
  title,
  displayTitle,
  id,
  sectionHeight, // Section height for filtering panel types
}) => {
  const columnReference = useRef(null);
  const { id: siteId } = useSelector(getActiveSite);
  const pagePanelTypeCounts = useSelector((state) =>
    getPagePanelTypeCounts(state, siteId, pageIndex)
  );

  const { panel: activePanelIndex = null } = activeTree;

  useEffect(() => {
    if (columnActive && columnReference.current && activePanelIndex === null) {
      columnReference.current.scrollIntoView();
    }
  }, [columnActive, activePanelIndex]);

  const createPanel = (event) => {
    event?.stopPropagation();

    const excludedPanelTypes = [...uniquePerPagePanelTypes].filter(
      (panelType) => (pagePanelTypeCounts.get(panelType) ?? 0) > 0
    );
    const defaultPanelType = getDefaultPanelType(getFilteredPanelTypes(excludedPanelTypes));

    setActiveTreeIndexes({ ...activeTree, panel: panels.length, initial: false });
    updateColumn(pageIndex, sectionIndex, columnIndex, (column) => ({
      ...column,
      panels: [
        ...column.panels,
        {
          id: `panel-${uuidv4()}`,
          title: DEFAULT_PANEL_TITLE,
          type: defaultPanelType,
        },
      ],
    }));
  };

  const movePanel = (from, to) => {
    updateColumn(pageIndex, sectionIndex, columnIndex, (column) => {
      const panel = column.panels[from];
      const updatedPanels = [...column.panels];
      updatedPanels.splice(from, 1);
      updatedPanels.splice(to, 0, panel);
      return {
        ...column,
        panels: updatedPanels,
      };
    });
    setActiveTreeIndexes({ ...activeTree, panel: to, initial: false });
  };

  const removePanel = (panelIndex) => {
    setActiveTreeIndexes({ ...activeTree, panel: undefined, initial: false });
    updateColumn(pageIndex, sectionIndex, columnIndex, (column) => {
      const updatedPanels = [...column.panels];
      updatedPanels.splice(panelIndex, 1);
      return {
        ...column,
        panels: updatedPanels,
      };
    });
  };

  const onUpdateColumn = useCallback(
    ({ formData }) => {
      updateColumn(pageIndex, sectionIndex, columnIndex, (column) => ({
        ...column,
        ...formData,
      }));
    },
    [updateColumn, pageIndex, sectionIndex, columnIndex]
  );

  const updatePanel = useCallback(
    (panelIndex, object) =>
      updateColumn(pageIndex, sectionIndex, columnIndex, (column) => ({
        ...column,
        panels: column.panels.map((panel, index) => (index === panelIndex ? object : panel)),
      })),
    [updateColumn, pageIndex, sectionIndex, columnIndex]
  );

  const toggleColumn = () => {
    if (columnActive) {
      setActiveTreeIndexes({
        ...activeTree,
        column: undefined,
        panel: undefined,
      });
    } else {
      setActiveTreeIndexes({
        ...activeTree,
        column: columnIndex,
      });
    }
  };

  const customValidate = useCallback((formData, errors) => {
    for (const fieldName of columnSchema.required) {
      const validValue = formData[fieldName];
      if (!validValue) {
        errors[fieldName].addError(`Column ${capitalize(fieldName)} is required`);
      }
    }
    return errors;
  }, []);

  return (
    <StyledItem
      ref={columnReference}
      itemClass="column-list-item"
      active={columnActive}
      onClick={toggleColumn}
      headerLabel={<TypeLabel type="column" />}
      headerContent={<div className="title">{title}</div>}
      headerActions={
        <>
          <ItemHandler
            move={moveColumn}
            index={columnIndex}
            count={columnCount}
          />
          <Icon icon={columnActive ? 'he-up' : 'he-down'} />
        </>
      }
    >
      <JsonEditor
        title="Column properties"
        formData={{ title, displayTitle, id }}
        schema={columnSchema}
        onFormSubmit={onUpdateColumn}
        saveButtonText={'Update column properties'}
        customValidate={customValidate}
        customTransformErrors={(errors) => transformErrors(errors)}
      />

      <Heading
        contentLeft={
          <>
            <div className="title">Panels</div>
            <Button
              onClick={createPanel}
              activity="secondary"
              slim
            >
              Create panel
            </Button>
          </>
        }
      />
      <ManagePanels
        activeTree={activeTree}
        panels={panels}
        updatePanel={updatePanel}
        movePanel={movePanel}
        removePanel={removePanel}
        columnActive={columnActive}
        setActiveTreeIndexes={setActiveTreeIndexes}
        columnCount={columnCount}
        columnIndex={columnIndex}
        sectionIndex={sectionIndex}
        sectionHeight={sectionHeight}
      />

      <Delete
        onDelete={() => removeColumn(columnIndex)}
        title="Delete column"
      />
    </StyledItem>
  );
};

export default ColumnItem;
